import { useState, useEffect } from "react";
import Picture from "@/components/ui/picture";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const images = [
  "/images/1140x640_3991.jpg.jpg",
  "/images/1270x710_124.jpg.jpg",
  "/images/1270x710_125.jpg.jpg",
];

const HeroSlider = () => {
  const { t } = useLanguage();
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const slides = t.heroSlider.slides.map((s, i) => ({ ...s, image: images[i] }));

  useEffect(() => {
    const timer = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrent((p) => (p + 1) % slides.length);
        setIsTransitioning(false);
      }, 300);
    }, 10000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const goTo = (index: number) => {
    setIsTransitioning(true);
    setTimeout(() => { setCurrent(index); setIsTransitioning(false); }, 300);
  };
  const prev = () => goTo((current - 1 + slides.length) % slides.length);
  const next = () => goTo((current + 1) % slides.length);

  return (
    <section className="relative h-[80vh] min-h-[520px] max-h-[750px] overflow-hidden">
      <div className={`absolute inset-0 transition-opacity duration-500 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
        <Picture src={slides[current].image} alt={slides[current].title} width={1270} height={710} fetchPriority={current === 0 ? "high" : undefined} decoding={current === 0 ? "sync" : "async"} className="w-full h-full object-cover animate-[kenBurns_18s_ease-in-out_infinite_alternate]" />
        <div className="absolute inset-0 avps-hero-overlay" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 via-transparent to-transparent" />
      </div>
      <div className="absolute inset-0 z-10 flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className={`max-w-2xl transition-all duration-500 ${isTransitioning ? 'opacity-0 translate-y-4' : 'opacity-100 translate-y-0'}`}>
            <h1 className="text-primary-foreground text-balance leading-tight">{slides[current].title}</h1>
            <p className="mt-5 text-base sm:text-lg md:text-xl text-primary-foreground/85 leading-relaxed max-w-xl">{slides[current].subtitle}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button variant="cta" size="lg" asChild><Link to="/diventa-volontario">{t.heroSlider.cta1}</Link></Button>
              <Button variant="hero-outline" size="lg" asChild><Link to="/sostienici">{t.heroSlider.cta3}</Link></Button>
            </div>
          </div>
        </div>
      </div>
      <button onClick={prev} className="hidden sm:block absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-primary-foreground/10 backdrop-blur-sm hover:bg-primary-foreground/20 text-primary-foreground transition-colors" aria-label={t.heroSlider.prevSlide}><ChevronLeft className="w-5 h-5" /></button>
      <button onClick={next} className="hidden sm:block absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-primary-foreground/10 backdrop-blur-sm hover:bg-primary-foreground/20 text-primary-foreground transition-colors" aria-label={t.heroSlider.nextSlide}><ChevronRight className="w-5 h-5" /></button>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {slides.map((_, i) => (
          <button key={i} onClick={() => goTo(i)} className="min-w-[24px] min-h-[24px] flex items-center justify-center" aria-label={t.heroSlider.goToSlide.replace("{n}", String(i + 1))}>
            <span className={`block rounded-full transition-all duration-300 h-2.5 ${i === current ? "bg-primary-foreground w-8" : "bg-primary-foreground/40 w-2.5"}`} />
          </button>
        ))}
      </div>
    </section>
  );
};

export default HeroSlider;
